import { useMemo } from 'react';

import { Box } from '#/components/Box/Box';
import { Checkbox } from '#/components/Checkbox/Checkbox';
import { getFieldQaId } from '#/components/Form/Field/getFieldQaId';
import { LargeCheckbox } from '#/components/LargeCheckbox/LargeCheckbox';

import type { CheckboxOption } from './CheckboxOption';
import { CheckboxContainer } from './Checkboxes.styled';

type Props = {
  id?: string;
  getValue: (optionName: string) => unknown;
  options: CheckboxOption[] | readonly CheckboxOption[];
  columns?: 1 | 2 | 3 | 4 | 5 | 6 | 7;
  hasError?: boolean;
  onChange: (checkboxName: string, value: boolean) => void;
  disabled?: boolean;
  value?: string;
  large?: boolean;
  fullWidth?: boolean;
  getQaId?: (checkboxName: string) => string;
  qaId: string;
};

export function Checkboxes({
  id,
  getValue,
  options,
  columns,
  hasError,
  onChange,
  disabled,
  value,
  large,
  fullWidth,
  getQaId,
  qaId,
}: Props) {
  const CheckboxComponent = useMemo(() => {
    if (large) {
      return LargeCheckbox;
    }
    return Checkbox;
  }, [large]);

  const getCheckboxQaId = getQaId ?? getFieldQaId;

  return (
    <CheckboxContainer
      $columns={columns}
      $large={large}
      $hasError={hasError}
      data-qa-id={qaId}
    >
      {options.map(({ label: checkboxLabel, name: checkboxName }, index) => (
        <Box
          key={checkboxName}
          pr={!columns && !large && index !== options.length - 1 ? '50px' : 0}
          p={large ? '6px' : undefined}
          width={columns && [1, 1, 1 / columns]}
        >
          <CheckboxComponent
            name={checkboxName}
            label={checkboxLabel}
            id={index === 0 ? (id ?? qaId) : `${id ?? qaId}-${checkboxName}`}
            qaId={`${qaId}-${getCheckboxQaId(checkboxName)}`}
            onChange={(e) => {
              onChange(checkboxName, e);
            }}
            checked={Boolean(getValue(checkboxName))}
            disabled={disabled}
            value={value}
            fullWidth={fullWidth}
          />
        </Box>
      ))}
    </CheckboxContainer>
  );
}
